<template>
    <section class="doc-section">

        <header class="header-container">
            <h1>Dispositivos a la Redonda</h1>
            <p class="description">
                Esta funcionalidad permite delimitar zonas geográficas específicas para identificar
                y listar de manera eficiente todos los dispositivos que se encuentran dentro de ellas en tiempo real.
            </p>
        </header>

        <div class="two-col">
            <div class="col-image">
                <div class="carousel-box">
                    <CarruselAciqui :items="imagenesBusquedaAreas" />
                </div>
            </div>

            <div class="col-steps">
                <p class="steps-label">Métodos de detección</p>
                
                <div class="highlight-card">
                    <div class="card-icon-box">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="currentColor"/>
                            <circle cx="12" cy="9" r="2.5" fill="white"/>
                        </svg>
                    </div>
                    <div>
                        <h2 class="card-title-inline">Búsqueda por Áreas</h2>
                        <p class="card-desc">
                            En la pestaña de <strong>Áreas</strong> se pueden encontrar dispositivos
                            dentro de un perímetro determinado, utilizando dos tipos de herramientas:
                        </p>
                    </div>
                </div>

                <div class="form-grid single-col mt-3">
                    <div class="field-row">
                        <span class="field-key">●</span>
                        <div>
                            <div class="field-label">Geocerca Circular</div>
                            <div class="field-desc">Define un radio de búsqueda en metros desde un punto central.</div>
                        </div>
                    </div>
                    <div class="field-row">
                        <span class="field-key">●</span>
                        <div>
                            <div class="field-label">Geocerca Poligonal</div>
                            <div class="field-desc">Dibuja áreas personalizadas con formas irregulares para mayor precisión.</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="section-divider">
            <h2>Referencia Operativa</h2>
        </div>

        <div class="doc-note note-info">
            <svg class="note-icon" width="15" height="15" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                <path d="M8 5v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                <circle cx="8" cy="11.5" r="0.75" fill="currentColor"/>
            </svg>
            <div class="note-body">
                <span class="note-label">Optimización de resultados</span>
                <p>Esta herramienta es ideal para logística y seguridad, permitiendo conocer qué unidades pueden atender un servicio basándose en su cercanía geográfica.</p>
            </div>
        </div>

    </section>
</template>

<script setup>
import CarruselAciqui from '../Carruselimg.vue';

const imagenesBusquedaAreas = [
    {
        nombre: "Búsqueda por Áreas",
        url: '/img/historicoDispositivo/Dispositivos_Redonda.png',
        descripcion: "Visualización de dispositivos detectados dentro de un área delimitada."
    }
]
</script>

<style scoped>
/* ── Estilos Base ─────────────────────────────────── */
.doc-section { padding: 20px; }
.description { font-size: 15px; opacity: 0.8; margin-bottom: 24px; line-height: 1.6; }

/* ── Layout dos columnas ──────────────────────────── */
.two-col { display: flex; gap: 32px; align-items: center; margin-bottom: 32px; }
.col-steps { flex: 1; }
.col-image  { flex: 1.2; }

/* ── Highlight Card (Soporte Dark Mode) ───────────── */
.highlight-card {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    background: var(--code-bg);
    border: 1px solid var(--border-color);
    border-left: 4px solid var(--primary-color);
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 16px;
}

.card-icon-box {
    width: 38px;
    height: 38px;
    background: var(--primary-color);
    color: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.card-title-inline {
    font-size: 16px;
    font-weight: 700;
    color: var(--primary-color);
    margin: 0 0 8px 0;
    border: none; /* Elimina bordes heredados de H2 generales */
    padding: 0;
}

.card-desc {
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
    opacity: 0.9;
}

/* ── UI Elements ─────────────────────────────────── */
.steps-label {
    font-size: 11px; font-weight: 700; letter-spacing: 0.07em;
    text-transform: uppercase; color: var(--primary-color); margin-bottom: 12px;
}

.section-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 32px 0 16px;
}

.section-divider h2 {
    font-size: 20px;
    font-weight: 700;
    color: var(--primary-color); /* Aquí es donde toma el azul */
    margin: 0;
}
.divider-line { flex: 1; height: 1px; background: var(--border-color); }

.carousel-box { 
    border: 1px solid var(--border-color); 
    padding: 8px; border-radius: 10px; 
    background: var(--code-bg); 
}

/* ── Grid de Métodos ──────────────────────────────── */
.form-grid.single-col { display: flex; flex-direction: column; gap: 8px; }
.field-row { 
    display: flex; gap: 12px; background: var(--code-bg); 
    border: 1px solid var(--border-color); border-radius: 8px; padding: 12px; 
}
.field-key { color: var(--primary-color); font-weight: bold; }
.field-label { font-weight: 600; font-size: 13.5px; }
.field-desc { font-size: 12.5px; opacity: 0.7; }

/* ── Notas ────────────────────────────────────────── */
.doc-note { display: flex; gap: 12px; padding: 16px; border-radius: 8px; border: 1px solid; margin-top: 16px; font-size: 14px; }
.note-info { background: var(--code-bg); border-color: var(--primary-color); color: var(--text-color); }
.note-label { color: var(--primary-color); font-weight: 700; text-transform: uppercase; font-size: 11px; display: block; }

/* ── Responsive ──────────────────────────────────── */
@media (max-width: 992px) {
    .two-col { flex-direction: column; }
    .col-image { order: -1; width: 100%; }
}
</style>